/**
 * CR155: Schedule Run Combined Review Page
 * Shows canonical deals across all sources for a schedule_run_id
 * Implements publish gating with warnings for failures
 */

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  ArrowLeft,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  Filter,
  ExternalLink,
  Globe,
  FileText,
  MessageSquare,
  Upload,
  AlertCircle,
  RefreshCw,
  Eye,
} from "lucide-react";
import { useScheduleRunReview, type CanonicalDealRow, type SourceRunSummary } from "@/hooks/useScheduleRunReview";
import { formatScheduleTime, formatScheduleDate, getUtcDebugString } from "@/lib/scheduler/timezoneUtils";
import { supabase } from "@/integrations/supabase/client";

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  WEB: <Globe className="h-4 w-4" />,
  FLYER: <FileText className="h-4 w-4" />,
  FORUM: <MessageSquare className="h-4 w-4" />,
};

function SourceStatusPill({ source }: { source: SourceRunSummary }) {
  const queueStatus = source.queue_status || 'PENDING';
  
  const statusConfig: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> = {
    ENQUEUED: { variant: "outline", icon: <Clock className="h-3 w-3" /> },
    RUNNING: { variant: "secondary", icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    COMPLETED: { variant: "default", icon: <CheckCircle2 className="h-3 w-3" /> },
    FAILED: { variant: "destructive", icon: <XCircle className="h-3 w-3" /> },
    PENDING: { variant: "outline", icon: <Clock className="h-3 w-3" /> },
  };

  const config = statusConfig[queueStatus] || statusConfig.PENDING;

  return (
    <Link to={`/admin/batch-runs/${source.batch_run_id}`}>
      <Badge variant={config.variant} className="gap-1 cursor-pointer hover:opacity-80">
        {SOURCE_ICONS[source.source_type] || <Globe className="h-3 w-3" />}
        {source.source_type}
        {config.icon}
        {source.deduped_count > 0 && (
          <span className="ml-1 text-xs">({source.deduped_count})</span>
        )}
      </Badge>
    </Link>
  );
}

function DealCard({ deal }: { deal: CanonicalDealRow }) {
  const canonical = deal.canonical_deal_json as {
    merchant_name?: string;
    deal_type?: string;
    headline_mechanic?: string;
    value?: {
      computed_value_cad?: number;
      effective_percent_back?: number;
    };
    dates?: {
      valid_from?: string;
      valid_until?: string;
    };
    publish?: {
      warnings?: string[];
      missing_fields?: string[];
    };
    brands?: {
      purchase_brand?: string;
      bonus_brand?: string;
    };
  };
  const sources = deal.merged_sources_json || [];
  const hasWarnings = (canonical.publish?.warnings?.length || 0) > 0 || 
                      (canonical.publish?.missing_fields?.length || 0) > 0;

  const formatValue = (): string => {
    if (canonical.value?.computed_value_cad) {
      return `$${canonical.value.computed_value_cad.toFixed(2)}`;
    }
    if (canonical.value?.effective_percent_back) {
      return `${canonical.value.effective_percent_back}%`;
    }
    return "—";
  };

  return (
    <div className={`border rounded-lg p-4 hover:bg-muted/30 transition-colors ${hasWarnings ? 'border-warning' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium">{canonical.merchant_name || 'Unknown'}</span>
            <Badge variant="outline" className="text-xs">{canonical.deal_type || 'DEAL'}</Badge>
            {hasWarnings && (
              <Tooltip>
                <TooltipTrigger>
                  <AlertTriangle className="h-4 w-4 text-warning" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Has validation warnings</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {canonical.headline_mechanic || 'No headline'}
          </p>
          <div className="flex items-center gap-2 mt-2">
            {sources.slice(0, 3).map((s, i) => (
              <Badge key={i} variant="secondary" className="text-xs gap-1">
                {SOURCE_ICONS[s.source_type] || '?'}
                {s.source_type}
              </Badge>
            ))}
            {sources.length > 3 && (
              <Badge variant="outline" className="text-xs">+{sources.length - 3}</Badge>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-semibold text-primary">{formatValue()}</div>
          {canonical.dates?.valid_until && (
            <div className="text-xs text-muted-foreground">
              Until {new Date(canonical.dates.valid_until).toLocaleDateString()}
            </div>
          )}
          <Badge 
            variant={deal.review_status === 'ACCEPTED' ? 'default' : 'secondary'} 
            className="text-xs mt-1"
          >
            {deal.review_status}
          </Badge>
        </div>
      </div>
    </div>
  );
}

export default function ScheduleRunReview() {
  const { scheduleRunId } = useParams<{ scheduleRunId: string }>();
  const {
    scheduleRun,
    batchRuns,
    canonicalDeals,
    counts,
    loading,
    error,
    refetch,
  } = useScheduleRunReview(scheduleRunId || null);

  const [searchTerm, setSearchTerm] = useState("");
  const [merchantFilter, setMerchantFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [showWarningsOnly, setShowWarningsOnly] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [failedPublishDialogOpen, setFailedPublishDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // Derive state for publish gating
  const isInProgress = counts.enqueued > 0 || counts.running > 0;
  const hasFailures = counts.failed > 0;
  const canPublish = !isInProgress && canonicalDeals.length > 0;
  const alreadyPublished = canonicalDeals.some(d => d.publish_status === 'PUBLISHED');

  // Get unique merchants and sources for filters
  const merchants = [...new Set(canonicalDeals.map(d => {
    const c = d.canonical_deal_json as { merchant_name?: string };
    return c.merchant_name || 'Unknown';
  }))].sort();

  const sourceTypes = [...new Set(batchRuns.map(b => b.source_type))].sort();

  // Filter deals
  const filteredDeals = canonicalDeals.filter(deal => {
    const canonical = deal.canonical_deal_json as {
      merchant_name?: string;
      headline_mechanic?: string;
      publish?: { warnings?: string[]; missing_fields?: string[] };
    };
    const sources = deal.merged_sources_json || [];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesMerchant = canonical.merchant_name?.toLowerCase().includes(search);
      const matchesHeadline = canonical.headline_mechanic?.toLowerCase().includes(search);
      if (!matchesMerchant && !matchesHeadline) return false;
    }

    // Merchant filter
    if (merchantFilter !== 'all' && canonical.merchant_name !== merchantFilter) {
      return false;
    }

    // Source filter
    if (sourceFilter !== 'all') {
      const hasSource = sources.some(s => s.source_type === sourceFilter);
      if (!hasSource) return false;
    }

    // Warnings filter
    if (showWarningsOnly) {
      const hasWarnings = (canonical.publish?.warnings?.length || 0) > 0 ||
                          (canonical.publish?.missing_fields?.length || 0) > 0;
      if (!hasWarnings) return false;
    }

    return true;
  });

  const handlePublishClick = () => {
    if (hasFailures) {
      setFailedPublishDialogOpen(true);
    } else {
      setPublishDialogOpen(true);
    }
  };

  const handlePublish = async () => {
    if (confirmText !== 'PUBLISH') {
      toast.error("Please type PUBLISH to confirm");
      return;
    }

    setPublishing(true);
    try {
      // Get batch run IDs for this schedule run
      const batchRunIds = batchRuns.map(b => b.batch_run_id);
      
      // Publish each batch run's deals
      let totalPublished = 0;
      for (const batchRunId of batchRunIds) {
        const { data, error } = await supabase.functions.invoke("batch-run-publish", {
          body: { batch_run_id: batchRunId },
        });
        if (error) {
          console.error(`Failed to publish batch run ${batchRunId}:`, error);
          continue;
        }
        totalPublished += data.published_count || 0;
      }

      toast.success("Published canonical deals.");
      setPublishDialogOpen(false);
      setFailedPublishDialogOpen(false);
      setConfirmText("");
      refetch();
    } catch (err) {
      console.error("Publish failed:", err);
      toast.error("Couldn't publish. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  if (loading && !scheduleRun) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !scheduleRun) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/scheduler">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Scheduler
            </Link>
          </Button>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error || 'Schedule run not found'}</AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    );
  }

  const displayDate = formatScheduleDate(scheduleRun.scheduled_for);
  const displayTime = formatScheduleTime(scheduleRun.scheduled_for, { includeDate: false, includeTimezone: true });
  const utcDebug = getUtcDebugString(scheduleRun.scheduled_for);

  return (
    <AdminLayout>
      <TooltipProvider>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" asChild>
                  <Link to="/admin/scheduler">
                    <ArrowLeft className="h-4 w-4" />
                  </Link>
                </Button>
                <h1 className="text-2xl font-bold">Review — Scheduled Run</h1>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-muted-foreground cursor-help">
                    Scheduled for {displayDate} at {displayTime}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{utcDebug}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={refetch} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handlePublishClick}
                disabled={!canPublish || alreadyPublished || publishing}
                className="gap-2"
              >
                {publishing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Publishing...
                  </>
                ) : isInProgress ? (
                  <>
                    <Clock className="h-4 w-4" />
                    Publish (processing…)
                  </>
                ) : alreadyPublished ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Already Published
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Publish canonical deals
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Source Status Pills */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Source Runs</CardTitle>
              <CardDescription>Status of each source collection run</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {batchRuns.length === 0 ? (
                  <span className="text-muted-foreground text-sm">No source runs found</span>
                ) : (
                  batchRuns.map((source) => (
                    <SourceStatusPill key={source.batch_run_id} source={source} />
                  ))
                )}
              </div>
              <Separator className="my-4" />
              <div className="flex gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="gap-1">
                    <Clock className="h-3 w-3" />
                    {counts.enqueued + counts.running}
                  </Badge>
                  <span className="text-muted-foreground">In Progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {counts.completed}
                  </Badge>
                  <span className="text-muted-foreground">Completed</span>
                </div>
                {counts.failed > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      {counts.failed}
                    </Badge>
                    <span className="text-muted-foreground">Failed</span>
                  </div>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <Badge variant="secondary">{counts.totalDeals}</Badge>
                  <span className="text-muted-foreground">Canonical Deals</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Banners */}
          {isInProgress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertTitle>Still processing</AlertTitle>
              <AlertDescription>
                Some sources are still running. Publishing will be available once all sources are finished.
              </AlertDescription>
            </Alert>
          )}

          {!isInProgress && hasFailures && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Some sources failed</AlertTitle>
              <AlertDescription className="flex items-center justify-between">
                <span>
                  You can still publish using the available canonical results. Published results may be incomplete.
                </span>
                <Button variant="link" size="sm" asChild className="text-destructive-foreground">
                  <Link to={`/admin/batch-runs?schedule_run_id=${scheduleRunId}`}>
                    View failed runs
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Link>
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search merchant or headline..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={merchantFilter} onValueChange={setMerchantFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All merchants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All merchants</SelectItem>
                    {merchants.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    {sourceTypes.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Switch
                    id="warnings-only"
                    checked={showWarningsOnly}
                    onCheckedChange={setShowWarningsOnly}
                  />
                  <Label htmlFor="warnings-only" className="text-sm cursor-pointer">
                    Has warnings
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Canonical Deals List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Canonical Deals
                <Badge variant="outline">{filteredDeals.length}</Badge>
              </CardTitle>
              <CardDescription>
                Deduplicated deals from all sources for this scheduled run
              </CardDescription>
            </CardHeader>
            <CardContent>
              {canonicalDeals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No canonical deals found for this scheduled run.
                </div>
              ) : filteredDeals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No deals match your filters.
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {filteredDeals.map(deal => (
                      <DealCard key={deal.id} deal={deal} />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Normal Publish Dialog */}
          <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Publish canonical deals</DialogTitle>
                <DialogDescription className="space-y-2">
                  <p>You are about to publish <strong>{canonicalDeals.length}</strong> canonical deals from this scheduled run.</p>
                  <p className="text-sm text-muted-foreground">
                    This will upsert deals into production by dedupe_key.
                  </p>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="confirm">Type <strong>PUBLISH</strong> to confirm</Label>
                  <Input
                    id="confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="PUBLISH"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handlePublish}
                  disabled={confirmText !== 'PUBLISH' || publishing}
                >
                  {publishing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    "Publish"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Failed Publish Warning Dialog */}
          <Dialog open={failedPublishDialogOpen} onOpenChange={setFailedPublishDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  Publish without all sources?
                </DialogTitle>
                <DialogDescription className="space-y-2">
                  <p>One or more sources failed. You can still publish using the available canonical results, but published results may be incomplete.</p>
                  <p className="text-sm">
                    <strong>{counts.failed}</strong> source run(s) failed, <strong>{counts.completed}</strong> completed successfully.
                  </p>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="confirm-failed">Type <strong>PUBLISH</strong> to confirm</Label>
                  <Input
                    id="confirm-failed"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="PUBLISH"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setFailedPublishDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handlePublish}
                  disabled={confirmText !== 'PUBLISH' || publishing}
                >
                  {publishing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    "Publish anyway"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </TooltipProvider>
    </AdminLayout>
  );
}
